import React from 'react';
import { PlanId } from '../lib/pricing';

const PLAN_NAMES: Record<PlanId, string> = {
  starter:      'Starter',
  professional: 'Professional',
  enterprise:   'Enterprise',
};

const PLAN_ACCENTS: Record<PlanId, { color: string; bg: string; border: string }> = {
  starter:      { color: '#60a5fa', bg: 'rgba(96,165,250,0.1)',  border: 'rgba(96,165,250,0.25)' },
  professional: { color: '#2FBF71', bg: 'rgba(47,191,113,0.1)',  border: 'rgba(47,191,113,0.25)' },
  enterprise:   { color: '#a78bfa', bg: 'rgba(167,139,250,0.1)', border: 'rgba(167,139,250,0.25)' },
};

interface UpgradeWallProps {
  requiredPlan: PlanId;
  featureName:  string;
  benefits:     string[];
}

export default function UpgradeWall({ requiredPlan, featureName, benefits }: UpgradeWallProps) {
  const accent = PLAN_ACCENTS[requiredPlan];

  return (
    <div style={overlay}>
      <div style={card}>
        <div style={{ ...icon, background: accent.bg, border: `1px solid ${accent.border}` }}>
          <LockIcon color={accent.color} />
        </div>
        <h2 style={title}>Función exclusiva del plan {PLAN_NAMES[requiredPlan]}</h2>
        <p style={subtitle}>
          <strong style={{ color: '#94a3b8' }}>{featureName}</strong> está disponible a partir del
          plan <strong style={{ color: accent.color }}>{PLAN_NAMES[requiredPlan]}</strong>.
        </p>
        <ul style={benefitsList}>
          {benefits.map((b, i) => (
            <li key={i} style={benefitItem}>
              <span style={{ ...check, color: accent.color }}>✓</span>
              {b}
            </li>
          ))}
        </ul>
        <a href="/pricing" style={{ ...cta, background: `linear-gradient(135deg, #1D6FA3 0%, ${accent.color} 100%)` }}>
          Ver planes →
        </a>
      </div>
    </div>
  );
}

function LockIcon({ color }: { color: string }) {
  return (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0110 0v4" />
    </svg>
  );
}

const overlay: React.CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 420,
  padding: '48px 24px',
  textAlign: 'center',
  background: 'rgba(15, 23, 42, 0.5)',
  backdropFilter: 'blur(4px)',
  borderRadius: 16,
  border: '1px solid #334155',
};

const card: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 14,
  maxWidth: 520,
};

const icon: React.CSSProperties = {
  width: 68,
  height: 68,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 4,
};

const title: React.CSSProperties = {
  color: '#f1f5f9',
  fontSize: '1.3rem',
  fontWeight: 700,
  margin: 0,
};

const subtitle: React.CSSProperties = {
  color: '#64748b',
  fontSize: '0.9rem',
  lineHeight: 1.7,
  margin: 0,
};

const benefitsList: React.CSSProperties = {
  listStyle: 'none',
  padding: '14px 18px',
  margin: '4px 0 0',
  display: 'flex',
  flexDirection: 'column',
  gap: 9,
  textAlign: 'left',
  background: 'rgba(255,255,255,0.03)',
  border: '1px solid #334155',
  borderRadius: 12,
  width: '100%',
};

const benefitItem: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 9,
  fontSize: '0.84rem',
  color: '#94a3b8',
  lineHeight: 1.4,
};

const check: React.CSSProperties = {
  fontWeight: 700,
  flexShrink: 0,
  marginTop: 1,
};

const cta: React.CSSProperties = {
  marginTop: 8,
  padding: '11px 28px',
  color: '#fff',
  borderRadius: 8,
  fontWeight: 700,
  fontSize: '0.9rem',
  textDecoration: 'none',
  display: 'inline-block',
};
